import { DateTime } from 'luxon';

import {
  MAPPER_KEY,
  SUPER_TYPE_KEYS,
  SUPER_SELF_MANAGED_FORM_UPLOAD_STATUSES,
  DECLARE_TRUE_AND_CORRECT_STATUSES,
  SUPER_TYPE_LABEL_MAP,
  YES_NO_LABEL_MAP,
  U18_NOT_ELIGIBLE_LABEL,
} from './superstep-constants';

export function isUnder18(dateOfBirth) {
  if (!dateOfBirth) return false;
  const dob = DateTime.fromISO(dateOfBirth);
  if (!dob.isValid) return false;
  const today = DateTime.now();
  const age = today.diff(dob, 'years').years;
  return Math.floor(age) < 18;
}

/**
 * Maps superannuation data from SuperAPI to indexed field structure
 * @param {Object} onboarding_session_super_selection - The superannuation selection data from SuperAPI
 * @param {Object} result - The result object to populate with mapped values
 * @param {string} [employeeDateOfBirth] - The employee's date of birth (ISO format)
 */
export function mapSuperannuationData(
  onboarding_session_super_selection,
  result,
  employeeDateOfBirth
) {
  if (!onboarding_session_super_selection) {
    return;
  }

  const hasNoFundSelection =
    !onboarding_session_super_selection.select_super_fund_product &&
    !onboarding_session_super_selection.self_managed_super_fund;

  if (isUnder18(employeeDateOfBirth) && hasNoFundSelection) {
    result[MAPPER_KEY.SUPER_OPTION].value = U18_NOT_ELIGIBLE_LABEL;
    return;
  }

  const {
    super_fund_member_detail,
    select_super_fund_product,
    self_managed_super_fund,
    generated,
  } = onboarding_session_super_selection;

  // Set eligibility
  result[MAPPER_KEY.SUPER_OPTION].value = YES_NO_LABEL_MAP.Yes;

  // Determine super type
  let superType = null;
  if (self_managed_super_fund) {
    superType = SUPER_TYPE_KEYS.SELF_MANAGED;
  } else if (select_super_fund_product?.is_default_selection) {
    superType = SUPER_TYPE_KEYS.EMPLOYER_NOMINATED;
  } else if (select_super_fund_product) {
    superType = SUPER_TYPE_KEYS.EMPLOYEE_NOMINATED;
  }
  result[MAPPER_KEY.SUPER_TYPE].value = superType
    ? SUPER_TYPE_LABEL_MAP[superType]
    : null;

  // Map member details
  if (super_fund_member_detail) {
    if (
      super_fund_member_detail.given_name &&
      super_fund_member_detail.family_name
    ) {
      result[
        MAPPER_KEY.SUPER_NAME
      ].value = `${super_fund_member_detail.given_name} ${super_fund_member_detail.family_name}`;
    }
    if (super_fund_member_detail.tfn) {
      result[MAPPER_KEY.SUPER_TFN_NUMBER].value = super_fund_member_detail.tfn;
    }
  }

  // Map super fund details - null if self-managed
  if (superType === SUPER_TYPE_KEYS.SELF_MANAGED) {
    result[MAPPER_KEY.SUPER_FUND].value = null;
  } else if (select_super_fund_product?.super_fund_product) {
    result[
      MAPPER_KEY.SUPER_FUND
    ].value = `${select_super_fund_product.super_fund_product.name} (USI: ${select_super_fund_product.super_fund_product.usi_number} / ABN: ${select_super_fund_product.super_fund_product.abn})`;

    if (select_super_fund_product.member_number) {
      result[MAPPER_KEY.SUPER_FUND_MEMBER].value =
        select_super_fund_product.member_number;
    }
  }

  // Handle SMSF form and details
  if (self_managed_super_fund) {
    if (
      generated?.super_choice_form_pdf_url ||
      generated?.super_choice_form_pdf_merged_url
    ) {
      result[MAPPER_KEY.SUPER_SELF_MANAGED_FORM].value =
        SUPER_SELF_MANAGED_FORM_UPLOAD_STATUSES.UPLOADED;
    } else {
      result[MAPPER_KEY.SUPER_SELF_MANAGED_FORM].value =
        SUPER_SELF_MANAGED_FORM_UPLOAD_STATUSES.NOT_UPLOADED;
    }

    // Map SMSF details
    if (self_managed_super_fund.fund_abn) {
      result[MAPPER_KEY.SUPER_SMSF_FUND_ABN].value =
        self_managed_super_fund.fund_abn;
    }
    if (self_managed_super_fund.fund_name) {
      result[MAPPER_KEY.SUPER_SMSF_FUND_NAME].value =
        self_managed_super_fund.fund_name;
    }
    if (self_managed_super_fund.member_number) {
      result[MAPPER_KEY.SUPER_SMSF_MEMBER_NUMBER].value =
        self_managed_super_fund.member_number;
    }
    if (self_managed_super_fund.member_name) {
      result[MAPPER_KEY.SUPER_SMSF_MEMBER_NAME].value =
        self_managed_super_fund.member_name;
    }
    if (self_managed_super_fund.fund_electronic_service_address) {
      result[MAPPER_KEY.SUPER_SMSF_ELECTRONIC_SERVICE_ADDRESS].value =
        self_managed_super_fund.fund_electronic_service_address;
    }
    if (self_managed_super_fund.bank_account_number) {
      result[MAPPER_KEY.SUPER_SMSF_BANK_ACCOUNT_NUMBER].value =
        self_managed_super_fund.bank_account_number;
    }
    if (self_managed_super_fund.bank_account_name) {
      result[MAPPER_KEY.SUPER_SMSF_BANK_ACCOUNT_NAME].value =
        self_managed_super_fund.bank_account_name;
    }
    if (self_managed_super_fund.bank_account_bsb) {
      result[MAPPER_KEY.SUPER_SMSF_BANK_ACCOUNT_BSB].value =
        self_managed_super_fund.bank_account_bsb;
    }
  }

  // Map declaration for employer or employee nominated super funds
  if (
    superType === SUPER_TYPE_KEYS.EMPLOYER_NOMINATED ||
    superType === SUPER_TYPE_KEYS.EMPLOYEE_NOMINATED
  ) {
    result[MAPPER_KEY.SUPER_DECLARE_TRUE_AND_CORRECT].value =
      DECLARE_TRUE_AND_CORRECT_STATUSES.CHECKED;
  } else {
    result[MAPPER_KEY.SUPER_DECLARE_TRUE_AND_CORRECT].value =
      DECLARE_TRUE_AND_CORRECT_STATUSES.NOT_CHECKED;
  }
}
